import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { FactCard } from "../components/FactCard";
import { SubjectIcon } from "../components/SubjectIcon";

const SubjectPage = () => {
  const { slug } = useParams();
  const [subject, setSubject] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [visible, setVisible] = useState(9);

  useEffect(() => {
    setSubject(null);
    setActiveTag(null);
    setVisible(9);
    api
      .get(`/subjects/${slug}`)
      .then((r) => setSubject(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif-display text-3xl font-bold text-[#9E4535]">Subject not found</h1>
          <Link to="/" className="font-ui text-sm text-[#9E4535] hover:underline mt-4 inline-block">Return to the vault</Link>
        </div>
      </Layout>
    );
  }

  if (!subject) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-6 py-24 text-center font-body text-[#7A6C5D]">Opening the drawer…</div>
      </Layout>
    );
  }

  const facts = activeTag
    ? subject.facts.filter((f) => (f.tags || []).includes(activeTag))
    : subject.facts;
  const shown = facts.slice(0, visible);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 font-mono-cv text-xs text-[#7A6C5D] mb-8" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#9E4535]">Vault</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A1615]">{subject.name}</span>
        </nav>

        {/* Header */}
        <header className="border-b border-[#D8CEB9] pb-8 mb-8">
          {subject.banner_url && (
            <div className="relative w-full h-40 sm:h-52 rounded-sm overflow-hidden mb-6 border border-[#D8CEB9]" data-testid="subject-banner">
              <img src={subject.banner_url} alt={`${subject.name} — CurioVault section`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1615]/50 to-transparent" />
            </div>
          )}
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 shrink-0 rounded-full border-2 border-[#C5A059] flex items-center justify-center text-[#9E4535]">
              <SubjectIcon name={subject.icon} className="w-7 h-7" />
            </div>
            <div>
              <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1615] leading-tight">
                {subject.name}
              </h1>
              <p className="font-body text-base text-[#4A4036] mt-3 max-w-2xl leading-relaxed">
                {subject.description}
              </p>
            </div>
          </div>
        </header>

        {/* Tag filters */}
        {subject.available_tags?.length > 0 && (
          <div className="mb-8" data-testid="tag-filters">
            <p className="caption-label mb-3">Filter by tag</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setActiveTag(null); setVisible(9); }}
                data-testid="tag-filter-all"
                className={`font-ui text-xs font-medium px-4 py-1.5 rounded-full border transition-colors ${
                  !activeTag
                    ? "bg-[#9E4535] text-[#FAF7F0] border-[#9E4535]"
                    : "bg-white text-[#4A4036] border-[#D8CEB9] hover:border-[#9E4535]"
                }`}
              >
                All
              </button>
              {subject.available_tags.map((t) => (
                <button
                  key={t}
                  onClick={() => { setActiveTag(t); setVisible(9); }}
                  data-testid={`tag-filter-${t}`}
                  className={`font-ui text-xs font-medium px-4 py-1.5 rounded-full border transition-colors ${
                    activeTag === t
                      ? "bg-[#9E4535] text-[#FAF7F0] border-[#9E4535]"
                      : "bg-white text-[#4A4036] border-[#D8CEB9] hover:border-[#9E4535]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Facts grid */}
        {facts.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {shown.map((f, i) => <FactCard key={f.slug} fact={f} index={i} />)}
            </div>
            {facts.length > visible && (
              <div className="text-center mt-10">
                <button
                  type="button"
                  data-testid="load-more-button"
                  onClick={() => setVisible((v) => v + 9)}
                  className="font-ui text-sm font-semibold px-8 py-3 bg-transparent text-[#9E4535] border border-[#9E4535] rounded-full hover:bg-[#9E4535] hover:text-[#FAF7F0] transition-colors"
                >
                  Load more curiosities ({facts.length - visible} left)
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="font-body text-[#4A4036] py-12 text-center">No curios with that tag yet.</p>
        )}
      </div>
    </Layout>
  );
};

export default SubjectPage;
